import { useCallback, useMemo, type ReactNode } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { useAuth, type User } from '@/hooks/use-auth'
import { BankAuthContext, type BankUser } from '@/lib/bank/auth'

function bankRole(user: User): BankUser['role'] | null {
  const backendRole = String(user.backendRole || '').trim().toLowerCase()
  if (backendRole === 'bank_admin') {
    return 'bank_admin'
  }
  if (backendRole === 'bank_officer') {
    return 'bank_officer'
  }
  if (!backendRole && user.role === 'bank') {
    return 'bank_officer'
  }
  return null
}

function toLegacyBankUser(user: User, role: BankUser['role']): BankUser {
  return {
    id: user.id,
    name: user.full_name || user.username,
    email: user.email,
    role,
    // The global profile does not expose canonical bank or branch scope.
    // Legacy APIs resolve tenancy from its real session token server-side.
    company_id: '',
    branch_ids: [],
    domain_roles: [],
  }
}

export function LegacyBankAuthRoute({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, isLoading, logout } = useAuth()
  const returnUrl = `${location.pathname}${location.search}`
  const role = user ? bankRole(user) : null

  const login = useCallback(async () => {
    navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`, {
      replace: true,
    })
  }, [navigate, returnUrl])

  const value = useMemo(
    () =>
      user && role
        ? {
            user: toLegacyBankUser(user, role),
            principal: null,
            isLoading: false,
            isAuthenticated: true,
            error: null,
            login,
            logout,
            resetIdleTimer: () => undefined,
          }
        : null,
    [login, logout, role, user],
  )

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        role="status"
      >
        Checking your bank session…
      </div>
    )
  }

  if (!user) {
    return (
      <Navigate
        to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`}
        replace
      />
    )
  }

  if (!value) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        role="alert"
      >
        Bank access is required.
      </div>
    )
  }

  return (
    <BankAuthContext.Provider value={value}>
      {children}
    </BankAuthContext.Provider>
  )
}
